import {
  Button,
  Divider,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Document, DocumentType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { DOCUMENT_TYPES_REQUIRING_APPROVAL, getMissingDocumentTypes } from '@pelicargo/utils';
import { Prisma } from '@prisma/client';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HiOutlineArrowUpTray, HiOutlineCheck } from 'react-icons/hi2';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { documentSchema } from '../../utils/schemas';
import { SimpleDropzone } from '../SimpleDropzone';
import { SimpleForm } from '../SimpleForm';
import { DocumentRow } from './DocumentRow';

type Props = {
  requestId?: number;
  accountId?: number;
  defaultDocuments?: Prisma.DocumentGetPayload<{ include: { account: true } }>[];
  documentTypes?: DocumentType[];
  isOpen: boolean;
  onClose: () => void;
};

type DocumentFormValues = {
  documents: Partial<Document>[];
};

export const DocumentsModal = ({
  requestId,
  accountId,
  isOpen,
  onClose,
  documentTypes,
  defaultDocuments: documents,
}: Props) => {
  const { mutateAsync: updateDocuments, isLoading } = trpc.updateDocuments.useMutation();
  const { mutateAsync: approveDocuments, isLoading: isApproving } = trpc.approveDocuments.useMutation();
  const { data: account } = trpc.getAccount.useQuery({ id: accountId }, { enabled: !!accountId });
  const methods = useForm<DocumentFormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(z.object({ documents: z.array(documentSchema).min(1) })),
    defaultValues: { documents },
  });
  const { request } = useCurrentRequest();
  const [isNotifyLoading, setIsNotifyLoading] = useState(false);

  const cleanedDocuments = useMemo(() => {
    const missingDocumentTypes = getMissingDocumentTypes(documents, documentTypes);

    // Include all current documents in the list. If a document type is not in the list, it will be added.
    const finalDocuments = [
      ...documents,
      ...missingDocumentTypes.map((type) => ({ type }) as Partial<Document>),
    ].filter((doc) => doc?.type || doc?.id);

    return finalDocuments;
  }, [documentTypes, documents]);

  const currentDocuments = methods.watch('documents').filter(Boolean);
  const updatedDocuments = currentDocuments.filter((doc) => {
    const existingDoc = documents.find((d) => d.id === doc?.id);
    return existingDoc && doc.type !== existingDoc.type;
  });
  const isUpdateButtonsDisabled = updatedDocuments.length === 0;
  const nonAccountDocuments = currentDocuments.filter((doc) => doc.account_id !== request?.account_id);
  const isApproveButtonDisabled = nonAccountDocuments
    .filter((doc) => DOCUMENT_TYPES_REQUIRING_APPROVAL.includes(doc.type))
    .every((doc) => !!doc.approved_at);
  const isOwnedByRequestAccount = accountId === request?.account_id;

  useEffect(() => {
    if (documents.length) methods.setValue('documents', documents);
  }, [documents, documents.length, methods]);

  const renderDocumentRow = (document: Partial<Document>) => <DocumentRow key={document.id} document={document} />;

  const handleUpdate = async () => {
    await updateDocuments({ documents: updatedDocuments.map((doc) => ({ id: doc.id, type: doc.type })) });

    onClose();
  };

  const handleUpdateAndNotify = async () => {
    try {
      const data = methods.getValues();
      setIsNotifyLoading(true);

      const updatedDocuments = data.documents.filter((doc) => {
        const existingDoc = documents.find((d) => d.id === doc?.id);
        return existingDoc && doc.type !== existingDoc.type;
      });

      await updateDocuments({
        documents: updatedDocuments.map((doc) => ({ id: doc.id, type: doc.type })),
        shouldNotify: true,
      });

      onClose();
    } finally {
      setIsNotifyLoading(false);
    }
  };
  const handleApproveAndNotify = async () => {
    const data = methods.getValues();
    const nonApprovedDocuments = data.documents.filter(
      (doc) => doc.id && !doc.approved_at && doc.account_id !== request?.account_id,
    );
    await approveDocuments({ documentIds: nonApprovedDocuments.map((doc) => doc.id), shouldNotify: true });

    onClose();
  };

  const documentAccountName = account?.name || '';

  return (
    <Modal size={{ base: 'full', lg: '4xl' }} isCentered isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent maxH={{ base: 'full', lg: '83vh' }} overflow="auto">
        <SimpleForm handleSubmit={handleUpdate} submitOnBlur={false} {...methods}>
          <ModalCloseButton />
          <ModalHeader>
            <Stack direction="row" align="center" justify="space-between">
              <Stack spacing="1">
                <Heading variant="h4">Manage {documentAccountName} Documents</Heading>
                <Text fontSize="lg" color="gray.500">
                  Upload, update and delete documents on behalf of {documentAccountName}.
                </Text>
              </Stack>
            </Stack>
          </ModalHeader>

          <ModalBody h="full">
            <Stack divider={<Divider />} spacing="4">
              <Stack>{cleanedDocuments.map(renderDocumentRow)}</Stack>
              <SimpleDropzone requestId={requestId} accountId={accountId} isTypeSelectable />
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Stack width="full" justify="flex-end" direction="row" spacing="3">
              <Button
                variant="outline"
                colorScheme="primary"
                title={isUpdateButtonsDisabled ? 'No documents to update' : undefined}
                onClick={handleUpdate}
                isLoading={isLoading && !isNotifyLoading}
                isDisabled={isUpdateButtonsDisabled}
                leftIcon={<HiOutlineArrowUpTray />}
              >
                Update
              </Button>
              <Button
                variant="solid"
                colorScheme="primary"
                type="submit"
                onClick={handleUpdateAndNotify}
                title={isUpdateButtonsDisabled ? 'No documents to update' : undefined}
                isLoading={isNotifyLoading}
                isDisabled={isUpdateButtonsDisabled}
                leftIcon={<HiOutlineArrowUpTray />}
              >
                Update & Notify
              </Button>
              <Show if={!isOwnedByRequestAccount}>
                <Button
                  variant="solid"
                  colorScheme="green"
                  title={isApproveButtonDisabled ? 'No documents to approve' : undefined}
                  onClick={handleApproveAndNotify}
                  isLoading={isApproving}
                  isDisabled={isApproveButtonDisabled}
                  leftIcon={<HiOutlineCheck />}
                >
                  Approve & Notify
                </Button>
              </Show>
            </Stack>
          </ModalFooter>
        </SimpleForm>
      </ModalContent>
    </Modal>
  );
};
